import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { Form } from 'antd';
import styled from 'styled-components';

import {
  getCmiDigestAdminUserPreference,
  getCmiDigestAdminUsersCommunities,
  updateCmiDigestAdminUserPreference,
} from 'apis/UserAPI';
import { useRootContext } from 'components/layout/RootContext';
import Loader from 'components/lib/Loader';

import { CMI_DIGEST_FREQUENCY_OPTIONS } from '../../../constants';
import CmiDigestForm from '../CmiDigestForm';
import { CmiDigestAdminUser, CmiDigestCommunity } from '../types';
import { showCmiDigestSelectionRequiredModal } from '../validation';
import CmiDigestModalWrapper from './CmiDigestModalWrapper';

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 60px 0;
`;

const CMI_DIGEST_INITIAL_VALUES = {
  enabled: false,
  frequency: CMI_DIGEST_FREQUENCY_OPTIONS[0].value,
  competitor_incentives_enabled: true,
  community_reviews_enabled: true,
  ai_instructions: '',
  communities: [] as number[],
};

type Props = {
  user: CmiDigestAdminUser;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
};

const CmiDigestUserModal = ({ user, isOpen, onClose, onSaved }: Props) => {
  const { showMessage } = useRootContext();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const { data: preference, isLoading: isLoadingPreference } = useQuery(['cmiDigestAdminUserPreference', user.id], () =>
    getCmiDigestAdminUserPreference(user.id)
  );

  const { data: communitiesResponse, isLoading: isLoadingCommunities } = useQuery(
    ['cmiDigestAdminUserCommunities', user.id],
    () => getCmiDigestAdminUsersCommunities([user.id])
  );

  const communities: CmiDigestCommunity[] = communitiesResponse?.[0]?.communities ?? [];

  useEffect(() => {
    if (preference) {
      form.setFieldsValue(preference);
    }
  }, [preference, form]);

  const handleSave = async () => {
    const values = await form.validateFields();

    if (values.enabled) {
      const missingCommunity = !values.communities || values.communities.length === 0;
      const missingContent = !values.competitor_incentives_enabled && !values.community_reviews_enabled;

      if (missingCommunity || missingContent) {
        showCmiDigestSelectionRequiredModal();
        return;
      }
    }

    setSaving(true);
    try {
      await updateCmiDigestAdminUserPreference(user.id, values);
      showMessage('success', 'CMI Digest settings updated successfully');
      onSaved();
    } catch (error) {
      showMessage('error', 'Failed to update CMI Digest settings');
    } finally {
      setSaving(false);
    }
  };

  const isLoading = isLoadingPreference || isLoadingCommunities;

  return (
    <CmiDigestModalWrapper
      title="CMI Digest"
      subtitle={`${user.name} · ${user.email}`}
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      disabled={saving || isLoading}
      communitiesToModify={[]}
      width={1100}
      saveButtonTestId="cmi-digest-modal-save-btn"
      cancelButtonTestId="cmi-digest-modal-cancel-btn"
    >
      {isLoading ? (
        <LoaderWrapper>
          <Loader active />
        </LoaderWrapper>
      ) : (
        <StyledForm form={form} initialValues={CMI_DIGEST_INITIAL_VALUES}>
          <CmiDigestForm form={form} communities={communities} isLoadingCommunities={false} hideDividers />
        </StyledForm>
      )}
    </CmiDigestModalWrapper>
  );
};

export default CmiDigestUserModal;
